import { forwardRef, ReactNode, useId, useRef } from 'react';
import { Button, ButtonProps } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { LongTextTooltip } from './long-text-tooltip';
import type { MenuItem, MenuItemOptions } from 'primereact/menuitem';
import { useFullscreenDialogParent } from '../general/use-fullscreen-dialog-parent';
import '../assets/disabled-actions.css';

type ExplainedButtonProps = ButtonProps & { disabledReason?: string | null };

/** A disabled control cannot receive focus or reliably receive mouse events. */
export const ExplainedButton = forwardRef<Button, ExplainedButtonProps>(function ExplainedButton({ disabledReason, ...props }, ref) {
    if (!(props.disabled || props.loading) || !disabledReason) return <Button {...props} ref={ref} />;
    const label = props['aria-label'] || props.label || props.title || (typeof props.tooltip === 'string' ? props.tooltip : 'Unavailable action');
    return <DisabledActionHint reason={disabledReason} label={label}>
        <Button {...props} ref={ref} title={undefined} tooltip={undefined} />
    </DisabledActionHint>;
});

export function DisabledActionHint({ reason, label, children }: { reason?: string | null; label: string; children: ReactNode }) {
    return reason ? <UnavailableAction reason={reason} label={label}>{children}</UnavailableAction> : <>{children}</>;
}

export function explainMenuItem<T extends MenuItem>(item: T, reason?: string | null): T {
    if (!item.disabled || !reason) return item;
    return { ...item, template: (entry: MenuItem, options: MenuItemOptions) => <DisabledActionHint label={item.label || 'Unavailable action'} reason={reason}>
        {typeof item.template === 'function' ? item.template(entry, options) : item.template ?? options.element}
    </DisabledActionHint> };
}

function UnavailableAction({ reason, label, children }: { reason: string; label: string; children: ReactNode }) {
    const id = `disabled-action-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
    const tooltip = useRef<Tooltip>(null);
    const parent = useFullscreenDialogParent();
    return <span id={id} className="disabled-action" tabIndex={0} role="group" aria-disabled="true"
        aria-label={label} aria-describedby={`${id}-reason`} onClick={event => event.stopPropagation()}
        onKeyDown={event => {
            if (event.key === 'Escape') tooltip.current?.hide();
            if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); }
        }}>
        {children}
        <span id={`${id}-reason`} hidden>{reason}</span>
        <LongTextTooltip ref={tooltip} target={`#${id}`} content={reason} event="both" position="top"
            appendTo={parent ?? document.body} baseZIndex={13000} className="disabled-action-tooltip" />
    </span>;
}
